import { useState } from "react";
import { cn } from "../../utils/cn";
import { Check, LucideIcon } from "lucide-react";

export interface FlowConfigOption {
  id: string;
  label: string;
  description: string;
  default?: boolean;
  icon?: LucideIcon;
}

export interface FlowConfigProps {
  flowType: string;
  options: FlowConfigOption[];
  onOptionsChange: (options: string[]) => void;
  className?: string;
}

export default function FlowConfig({
  flowType,
  options,
  onOptionsChange,
  className,
}: FlowConfigProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    options.filter((opt) => opt.default).map((opt) => opt.id),
  );

  const handleOptionToggle = (optionId: string) => {
    const newSelected = selectedOptions.includes(optionId)
      ? selectedOptions.filter((id) => id !== optionId)
      : [...selectedOptions, optionId];
    setSelectedOptions(newSelected);
    onOptionsChange(newSelected);
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div className="border border-[var(--color-fg)]/20 rounded-lg p-4">
        <h3 className="font-semibold text-xl mb-3">{flowType} Options</h3>
        <div className="grid grid-cols-1 gap-2">
          {options.map((option) => {
            const isSelected = selectedOptions.includes(option.id);
            const Icon = option.icon;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleOptionToggle(option.id)}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg text-left w-full transition-all duration-200 border",
                  isSelected
                    ? "bg-blue-500/10 border-blue-500/30"
                    : "bg-transparent border-[var(--color-fg)]/10 hover:border-[var(--color-fg)]/20 hover:bg-[var(--color-fg)]/5",
                )}
              >
                <div
                  className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200",
                    isSelected
                      ? "bg-blue-500/20 text-blue-400"
                      : "bg-slate-800/50 text-slate-500",
                  )}
                >
                  {Icon ? (
                    <Icon className="w-4 h-4" />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-current" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div
                    className={cn(
                      "font-medium text-base leading-tight transition-colors duration-200",
                      isSelected ? "text-white" : "text-slate-300",
                    )}
                  >
                    {option.label}
                  </div>
                  <div className="text-sm text-[var(--color-fg)]/50 mt-0.5 leading-relaxed">
                    {option.description}
                  </div>
                </div>

                <div
                  className={cn(
                    "w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-all duration-200",
                    isSelected
                      ? "bg-blue-500/20 border border-blue-500/40 text-blue-400"
                      : "border border-slate-600/50",
                  )}
                >
                  {isSelected && <Check className="w-3 h-3" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
